import React, { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import logo from "../components/assets/real_logo.svg";
import cbn from "../components/assets/cbn.svg";
import icnaira from "../components/assets/ic-naira.svg";
import config from "../config.json";
import {
  initiateThirdPartyInsurance,
  initiateThirdPartyRide,
  initiateThirdPartyShop,
} from "../services/interface/InterfaceService";
import { Fire } from "../utils/Fire";
import { useLocation, useNavigate } from "react-router-dom";
import Success from "./Success";
import Decline from "./Decline";
import { formatAmount } from "../components/reusables/FormtMoney";
import InsuranceFaqModal from "../components/FAQ-insurance";
import { setAccessToken } from "../utils/config";
import { Button } from "@headlessui/react";
import CalculatorModal from "../components/CalculatorModal";

const MAX_COMPREHENSIVE_VEHICLE_AMOUNT = 100000000;
const FORM_STATE_STORAGE_KEY = "gateway_home_form_state";
const TERMS_STATE_STORAGE_KEY = "gateway_home_terms_state";
const DEFAULT_FORM_DATA = {
  amount: "",
  emailAddress: "",
  insuranceType: "",
  vehicleAmount: "",
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};
const Home = () => {
  // const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [openCommentid, setOpenCommentid] = useState(false);
  const [isOpenid, setIsOpenid] = useState(true);
  const [formData, setFormData] = useState(() => {
    try {
      const savedFormData = sessionStorage.getItem(FORM_STATE_STORAGE_KEY);
      if (!savedFormData) return DEFAULT_FORM_DATA;
      return { ...DEFAULT_FORM_DATA, ...JSON.parse(savedFormData) };
    } catch (storageError) {
      return DEFAULT_FORM_DATA;
    }
  });
  const [agreeToTerms, setAgreeToTerms] = useState(() => {
    try {
      return sessionStorage.getItem(TERMS_STATE_STORAGE_KEY) === "true";
    } catch (storageError) {
      return false;
    }
  });
  const [errorMessages, setErrorMessages] = useState("");
  const [error, setError] = useState({
    amount: false,
    emailAddress: false,
    vehicleAmount: false,
    insuranceType: false,
    agreeToTerms: false,
  });
  const query = useQuery();
  const [status, setStatus] = useState("none");
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenCalculator, setIsOpenCalculator] = useState(false);
  const [isVehicleLimitModalOpen, setIsVehicleLimitModalOpen] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(FORM_STATE_STORAGE_KEY, JSON.stringify(formData));
    } catch (storageError) {
      // Ignore write failures in private mode/restricted contexts.
    }
  }, [formData]);

  useEffect(() => {
    try {
      sessionStorage.setItem(TERMS_STATE_STORAGE_KEY, String(agreeToTerms));
    } catch (storageError) {
      // Ignore write failures in private mode/restricted contexts.
    }
  }, [agreeToTerms]);

  const vehicleAmountValue =
    parseFloat((formData.vehicleAmount || "").replace(/,/g, "")) || 0;
  const isComprehensiveVehicleAboveLimit =
    formData.insuranceType === "comprehensive" &&
    vehicleAmountValue > MAX_COMPREHENSIVE_VEHICLE_AMOUNT;

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const CalculatorToggle = () => {
    if (isComprehensiveVehicleAboveLimit) {
      setIsVehicleLimitModalOpen(true);
      return;
    }
    setIsOpenCalculator(!isOpenCalculator);
  };

  useEffect(() => {
    const amount = parseFloat(formData.vehicleAmount.replace(/,/g, "")) || 0;
    if (formData.insuranceType === "third-party") {
      // ✅ Fixed amount: 15,000 for third-party
      setFormData((prevData) => ({
        ...prevData,
        amount: formatAmount("15000.00"),
      }));
    } else if (formData.insuranceType === "comprehensive" && amount > 0) {
      // ✅ Dynamic: 5% of vehicle amount for comprehensive
      const calculatedPremium = amount * 0.05;
      setFormData((prevData) => ({
        ...prevData,
        amount:
          calculatedPremium > 0
            ? formatAmount(calculatedPremium.toFixed(2).toString())
            : "",
      }));
    } else {
      // ✅ Reset if no type selected
      setFormData((prevData) => ({
        ...prevData,
        amount: "",
      }));
    }
    //console.log("Calculated Amount:", formData.amount);
  }, [formData.insuranceType, formData.vehicleAmount]);

  // useEffect(() => {
  //   setAccessToken(config.apiSecret)
  //   const fetchMerchantAndInsuranceDetails = async () => {
  //     if (config.appName === "insurance") {
  //       try {
  //         // Fetch merchant details
  //         const merchantResponse = await getMerchantDetails();

  //         // If the merchant is an agent, fetch insurance companies
  //         if (merchantResponse?.isAgent) {
  //           const insuranceResponse = await listInsuranceCompany();
  //           setInsuranceData(insuranceResponse);
  //           setShowInsuranceCompany(true);
  //         }
  //       } catch (error) {
  //         console.error('Error fetching data:', error);
  //       }
  //     }
  //   };

  //   fetchMerchantAndInsuranceDetails();
  // }, [config.appName]);

  useEffect(() => {
    setAccessToken(config.apiSecret);
  }, [config.appName]);

  const validateForm = () => {
    let errors = {};

    if (!formData.amount) {
      errors.amount = "Amount is required.";
    } else if (
      isNaN(formData.amount.replace(/,/g, "")) ||
      parseFloat(formData.amount.replace(/,/g, "")) <= 0
    ) {
      errors.amount = "Amount must be a positive number.";
    }

    if (!formData.emailAddress) {
      errors.emailAddress = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.emailAddress)) {
      errors.emailAddress = "Email address is invalid.";
    }

    if (!agreeToTerms) {
      errors.agreeToTerms = "Accept the Terms and Conditions to proceed.";
    }

    if (formData.insuranceType === "comprehensive") {
      if (!formData.vehicleAmount) {
        errors.vehicleAmount =
          "Vehicle amount is required for third-party insurance.";
      } else if (
        isNaN(formData.vehicleAmount.replace(/,/g, "")) ||
        parseFloat(formData.vehicleAmount.replace(/,/g, "")) <= 0
      ) {
        errors.vehicleAmount = "Vehicle amount must be a positive number.";
      } else if (
        parseFloat(formData.vehicleAmount.replace(/,/g, "")) >
        MAX_COMPREHENSIVE_VEHICLE_AMOUNT
      ) {
        errors.vehicleAmount =
          "Vehicle amount must be N100,000,000.00 or below.";
      }
    }

    //  if(config.appName === "insurance" && showInsuranceCompany){
    //   if (!formData.insuranceCompany || formData.insuranceCompany === "") {
    //     errors.insuranceCompany = "Choose an Insurance company.";
    //   }
    //  }

    return errors;
  };

  useEffect(() => {
    setTimeout(() => {
      setOpenCommentid(true);
    }, 500);
    const refStatus = query.get("status");
    setStatus(refStatus);
  }, [query]);

  const handleOpenCloseIdinfo = () => {
    setIsOpenid(!isOpenid);
  };

  useEffect(() => {
    if (isComprehensiveVehicleAboveLimit) {
      setIsVehicleLimitModalOpen(true);
      setIsOpenCalculator(false);
      setError((prevError) => ({
        ...prevError,
        vehicleAmount: "Vehicle amount must be N100,000,000.00 or below.",
      }));
      return;
    }

    setIsVehicleLimitModalOpen(false);
    setError((prevError) => ({
      ...prevError,
      vehicleAmount: false,
    }));
  }, [isComprehensiveVehicleAboveLimit]);

  const formatAmountWithDecimals = (value) => {
    let cleaned = value.replace(/[^0-9.]/g, "");
    let numValue = parseFloat(cleaned);

    if (isNaN(numValue)) {
      return "";
    }

    // Always show 2 decimal places with thousand separators
    return numValue.toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "amount" || name === "vehicleAmount") {
      let cleanedValue = value.replace(/[^0-9.]/g, "");
      const [integerPart, decimalPart] = cleanedValue.split(".");
      if (integerPart.length <= 9) {
        if (decimalPart && decimalPart.length >= 3) {
          return;
        }
        const formattedValue = formatAmount(cleanedValue);
        setFormData({
          ...formData,
          [name]: formattedValue,
        });
        setError({
          ...error,
          amount: false,
          vehicleAmount: false,
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
      setError({
        ...error,
        emailAddress: false,
      });
    }
  };

  const handleAmountBlur = () => {
    const cleanedAmount = formData.vehicleAmount.replace(/,/g, "");
    const amount = parseFloat(cleanedAmount);

    if (!isNaN(amount) && amount > 0) {
      const formattedValue = formatAmountWithDecimals(cleanedAmount);
      setFormData({
        ...formData,
        vehicleAmount: formattedValue,
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isComprehensiveVehicleAboveLimit) {
      setIsVehicleLimitModalOpen(true);
      setError((prevError) => ({
        ...prevError,
        vehicleAmount: "Vehicle amount must be N100,000,000.00 or below.",
      }));
      return;
    }
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      // Proceed with payment logic
      handlePayNow();
    } else {
      setError(errors);
      setErrorMessages(errors);
    }
  };

  const handlePayNow = async () => {
    try {
      const prefix = config.merchantPrefix;
      const reference = Math.random().toString(36).substring(5);
      const suffix = new Date().getTime();
      const fullRreference = `${prefix}${reference}${suffix}`;

      let request = {};
      if (config.appName === "insurance") {
        request = {
          amount: formData.amount.replace(/,/g, ""),
          emailAddress: formData.emailAddress,
          identifier: fullRreference,
          redirectUrl: config.redirectUrl,
          insuranceType:
            formData.insuranceType == "comprehensive"
              ? "Comprehensive"
              : "Thirdparty",
        };
      } else {
        request = {
          amount: formData.amount.replace(/,/g, ""),
          emailAddress: formData.emailAddress,
          identifier: fullRreference,
          redirectUrl: config.redirectUrl,
        };
      }

      // const request = {
      //   amount: formData.amount.replace(/,/g, ''),
      //   emailAddress: formData.emailAddress,
      //   identifier: fullRreference,
      //   redirectUrl: encodeURIComponent(config.redirectUrl),
      // };

      setLoading(true);
      let response = {};

      switch (config.appName) {
        case "shop":
          response = await initiateThirdPartyShop(request);
          break;
        case "insurance":
          response = await initiateThirdPartyInsurance(request);
          break;
        case "ride":
          response = await initiateThirdPartyRide(request);
          break;
        default:
          Fire("error", "Invalid Configuration, Please contact support");
          break;
      }
      if (response && response.redirectionUrl) {
        window.open(response.redirectionUrl, "_blank");
      }
    } catch (error) {
      Fire(
        "error",
        "Unable to process your request at the moment, Please try again later",
      );
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // && merchant?.isAgent &&

  return (
    <>
      {status === "success" ? (
        <Success />
      ) : status === "failed" ? (
        <Decline />
      ) : (
        <div className="flex min-h-screen items-center justify-center bg-transparent bg-gray-600">
          <div className="rounded-lg bg-white md:w-[700px] w-full  border-[1px] border-gray-200 shadow-sm px-5 py-5 relative pb-10 mx-auto text-center">
            <div className="flex flex-col pt-5 items-center text-center justify-center md:w-[75%] w-full md:mx-auto md:my-auto md:px-10">
              <div className="self-center mb-7">
                <img src={logo} alt="logo" />
              </div>
              <div className=" flex md:w-[100%] w-full relative justify-center flex-row">
                <div>
                  {config.appName === "insurance" ? (
                    <h1 className="md:text-2xl text-xl font-bold text-gray-900 mb-4 capitalize ">
                      insurance
                      <span className="text-red-600"> premium financing</span>
                    </h1>
                  ) : (
                    <h2>Pay in instalment in three step</h2>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end gap-5 mt-5 w-full ">
                <Button
                  onClick={toggleModal}
                  className="bg-[#353535] text-white flex items-center justify-center py-2 px-3 rounded-full mb-5 hover-pointer gap-7 md:text-sm text-sm font-medium w-fit"
                >
                  <p>How it works</p>
                </Button>
              </div>
              <form
                onSubmit={handleSubmit}
                className="w-[90%] px-3 rounded-md py-2 flex flex-col items-center"
              >
                {
                  config.appName === "insurance" && <div className="w-full">
                  <label
                    htmlFor="hs-leading-icon"
                    className="block text-left text-sm font-medium mb-1"
                  >
                    Insurance Type
                  </label>
                  <div className="relative mb-5">
                    <select
                      className="bg-gray-20 border-gray-300 h-12 py-2 outline-none  p-3 w-full border rounded-md"
                      name="insuranceType"
                      id="insurance-type"
                      onChange={handleChange}
                      value={formData.insuranceType}
                    >
                      <option value="">Select an option</option>
                      <option value="comprehensive">
                        Comprehensive Insurance
                      </option>
                      <option value="third-party">
                        ThirdParty Motor Insurance
                      </option>
                    </select>
                  </div>
                </div>
                }
                {formData.insuranceType === "comprehensive" && config.appName === "insurance" && (
                  <div className="w-full">
                    <label
                      htmlFor="hs-leading-icon"
                      className="block text-left text-sm font-medium mb-1"
                    >
                      Vehicle Amount
                    </label>
                    <div className="relative mb-5">
                      <input
                        type="text"
                        name="vehicleAmount"
                        value={formData.vehicleAmount}
                        onChange={handleChange}
                        onBlur={handleAmountBlur}
                        className="bg-gray-20 border-gray-300 h-12 py-2 pl-10 outline-none w-full border rounded-md"
                        placeholder="Enter vehicle amount"
                        inputMode="decimal"
                      />
                      <div className="absolute top-5 left-0 flex items-center justify-center pointer-events-none z-20 ps-4">
                        <img
                          src={icnaira}
                          alt="in-naira"
                          className="w-3 h-3 object-contain"
                        />
                      </div>
                      {error.vehicleAmount && (
                        <p className="text-red-500 text-sm text-left">
                          {error.vehicleAmount}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                  <div className="w-full">
                    <label
                      htmlFor="hs-leading-icon"
                      className="block text-left text-sm font-medium mb-1"
                    >
                      {config.appName === "insurance" && formData.insuranceType != "" && formData.insuranceType === "comprehensive"
                        ? "Insurance Premium (5% of vehicle amount)"
                        : "Amount"}
                    </label>
                    <div className="relative mb-5">
                      <input
                        type="text"
                        name="amount"
                        disabled={config.appName === "insurance"}
                        value={formData.amount}
                        onChange={handleChange}
                        className="bg-gray-20 border-gray-300 h-12 py-2 px-8 outline-none  p-3 w-full border rounded-md"
                        placeholder={
                          formData.insuranceType === "comprehensive"
                            ? "0.00"
                            : "Enter amount"
                        }
                      />

                      <div className="absolute inset-y-0 start-0 flex items-center justify-center pointer-events-none z-20 ps-4">
                        <img
                          src={icnaira}
                          alt="in-naira"
                          className="w-3 h-3 object-contain"
                        />
                      </div>
                      {error.amount && (
                        <p className="text-red-500 text-sm text-left">
                          {error.amount}
                        </p>
                      )}
                    </div>
                  </div>


                <div className="w-full">
                  <label
                    htmlFor="hs-leading-icon"
                    className="block text-left text-sm font-medium mb-1"
                  >
                    Email Address
                  </label>
                  <div className="relative mb-5">
                    <input
                      type="email"
                      name="emailAddress"
                      value={formData.emailAddress}
                      onChange={handleChange}
                      className="bg-gray-20 border-gray-300 h-12 py-2 px-8 outline-none p-3 w-full border rounded-md"
                      placeholder="Enter email address"
                    />

                    <div className="absolute inset-y-0 start-0 flex items-center pointer-events-none z-20 ps-4">
                      <svg
                        className="shrink-0 size-4 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                      </svg>
                    </div>
                    {error.emailAddress && (
                      <p className="text-red-500 text-sm text-left">
                        {error.emailAddress}
                      </p>
                    )}
                  </div>
                </div>

                {/* {showInsuranceCompany &&
                    (
                      <div className="w-full">
                      <label
                        htmlFor="hs-leading-icon"
                        className="block text-left text-sm font-medium mb-1"
                      >
                       Insurance companies
                      </label>
                      <div className="relative mb-5">
                       <select value={formData.insuranceCompany}
                        onChange={handleChange} name="insuranceCompany" id="insuranceCompany"  className="bg-gray-20 border-gray-300 font-light text-gray-400 h-12 py-2 px-8 outline-none p-3 w-full border rounded-md">
                       <option value="">Select an option</option>
                        {
                          insuranceData?.map((item, index) => (
                            <option key={index} value={item}>{item}</option>
                          ))
                        }
                       </select>
                        <div className="absolute inset-y-0 start-0 flex items-center pointer-events-none z-20 ps-4">
                        <svg
                            className="shrink-0 size-4 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 2L4 5v6c0 5.25 3.75 10 8 10s8-4.75 8-10V5l-8-3z" />
                          </svg>
  
                        </div>
                        {error.insuranceCompany && (
                          <p className="text-red-500 text-sm text-left">{error.insuranceCompany}</p>
                        )}
                      </div>
  
                    </div>
                    )
                  } */}

                {/* Terms and Conditions Section */}
                {
                  config.appName === "insurance" && (
                    <div className="w-full mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="agreeToTerms"
                      checked={agreeToTerms}
                      onChange={(e) => {
                        setAgreeToTerms(e.target.checked);
                        setError({
                          ...error,
                          agreeToTerms: false,
                        });
                      }}
                      className="mt-1 w-5 h-5 cursor-pointer accent-[#161032]"
                    />
                    <label
                      htmlFor="agreeToTerms"
                      className="text-sm text-gray-700 cursor-pointer flex-1"
                    >
                      By ticking the checkbox, I accept that I have read and
                      agree to the{" "}
                      <button
                        type="button"
                        onClick={() => navigate("/terms-and-conditions")}
                        className="text-[#161032] font-semibold hover:text-red-500 underline transition"
                      >
                        Terms and Conditions
                      </button>
                    </label>
                  </div>
                  {error.agreeToTerms && (
                    <p className="text-red-500 text-sm mt-2 ml-8">
                      {error.agreeToTerms}
                    </p>
                  )}
                </div>
                  )
                }

                <button
                  type="submit"
                  disabled={loading || isComprehensiveVehicleAboveLimit}
                  className={`h-12 font-semibold text-lg flex items-center justify-center text-white py-3 mt-5 rounded-lg transition ease-in-out duration-300 w-full ${
                    loading || isComprehensiveVehicleAboveLimit
                      ? "bg-red-300 cursor-not-allowed"
                      : "bg-[red] hover:bg-[#161032]"
                  }`}
                >
                  {loading ? "Connecting..." : "Get financing"}
                </button>
                {config.appName === "insurance" && formData.amount && (
                  <button
                    onClick={CalculatorToggle}
                    type="button"
                    disabled={isComprehensiveVehicleAboveLimit}
                    className={`h-12 font-semibold text-lg flex items-center justify-center text-white py-3 mt-5 rounded-lg transition ease-in-out duration-300 w-full ${
                      isComprehensiveVehicleAboveLimit
                        ? "bg-[#161032]/40 cursor-not-allowed"
                        : "bg-[#161032] hover:bg-[red]"
                    }`}
                  >
                    Calculate Monthly Repayment
                  </button>
                )}
              </form>
              <div className="text-center mt-12">
                <p className="text-[#001F54] text-sm">Powered by</p>
                <div className="flex items-center gap-1 mt-1">
                  <img className="w-4" src={logo} alt="cbn" />
                  <p className="text-[#001F54] text-sm font-semibold">
                    Premium<span className="text-red-500">Trust </span> Bank
                  </p>
                </div>
              </div>
              <InsuranceFaqModal isOpen={isOpen} setIsOpen={setIsOpen} />

              {isVehicleLimitModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-2">
                  <div className="relative bg-white p-6 rounded-lg lg:w-[40%] w-[95%] shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-semibold text-[#161032]">
                        Vehicle Amount Limit
                      </h2>
                      <button
                        type="button"
                        className="text-gray-500 text-3xl font-bold leading-none hover:text-black"
                        onClick={() => setIsVehicleLimitModalOpen(false)}
                      >
                        &times;
                      </button>
                    </div>

                    <p className="text-sm text-gray-700 leading-6">
                      Insurance premium financing on the platform is for
                      vehicles of N100,000,000.00 and below. If you would like
                      to get insurance premium financing for a vehicle above
                      N100,000,000.00, kindly send an email to{" "}
                      <a
                        href="mailto:digitaloperations@premiumtrustbank.com"
                        className="font-semibold text-[#161032] underline hover:text-red-500"
                      >
                        digitaloperations@premiumtrustbank.com
                      </a>{" "}
                      and we will reach out to you.
                    </p>

                    <button
                      type="button"
                      onClick={() => setIsVehicleLimitModalOpen(false)}
                      className="bg-[#161032] h-12 font-semibold text-lg flex items-center justify-center text-white py-3 mt-6 rounded-lg hover:bg-[red] transition ease-in-out duration-300 w-full"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              <CalculatorModal
                isOpen={isOpenCalculator}
                setIsOpen={setIsOpenCalculator}
                amount={formData?.amount}
              />

              <div className="flex w-full justify-center text-center items-center gap-2 mt-12 font-medium">
                <img src={cbn} alt="cbn" />
                <p className="text-[#001F54] md:text-md text-sm">
                  Licensed by the Central Bank of Nigeria
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;
